import React, { useState } from 'react';
import {
  Table, Card, Button, Space, Modal, Form, 
  Input, Tree, message, Popconfirm, Typography,
  Tooltip, Tag
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
const { Title } = Typography;

const { TextArea } = Input;

// 辨识标签映射：平台=红，企业=蓝
const IDENTIFY_TAG_MAP = {
  platform: { label: '平台', color: 'red' },
  enterprise: { label: '企业', color: 'blue' },
};
const IDENTIFY_TAG_ORDER = ['platform', 'enterprise'];

// 判定当前登录账号类型（平台管理 / 企业）。TODO: 联调时替换为登录账号类型字段
const getAccountType = () => {
  try {
    const info = JSON.parse(localStorage.getItem('user_info') || '{}');
    return info?.accountType || info?.type || 'platform';
  } catch (e) {
    return 'platform';
  }
};

const GeneralRoleManagement = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();
  const [checkedKeys, setCheckedKeys] = useState([]);

  // 仅平台管理账号展示辨识标签，企业账号不展示
  const showIdentifyTag = getAccountType() !== 'enterprise';

  // 在树形菜单节点行内展示辨识 tag（仅菜单类型节点）
  const renderTitle = (node) => {
    if (!showIdentifyTag) return node.title;
    const tags = node.identifyTags || [];
    return (
      <span>
        {node.title}
        {tags.length > 0 && (
          <span style={{ marginLeft: 6 }}>
            {IDENTIFY_TAG_ORDER.filter((v) => tags.includes(v)).map((v) => {
              const conf = IDENTIFY_TAG_MAP[v];
              return <Tag key={v} color={conf.color} style={{ marginLeft: 4 }}>{conf.label}</Tag>;
            })}
          </span>
        )}
      </span>
    );
  };

  const decorateTreeData = (nodes) => nodes.map((node) => ({
    ...node,
    title: renderTitle(node),
    children: node.children ? decorateTreeData(node.children) : undefined,
  }));

  // 模拟菜单树数据
  const menuTreeData = [
    {
      title: '首页',
      key: 'home',
      identifyTags: ['platform', 'enterprise'],
      children: [],
    },
    {
      title: '设备中心',
      key: 'device',
      children: [
        { title: '设备管理', key: 'device-list', identifyTags: ['platform', 'enterprise'] },
        { title: '设备详情', key: 'device-detail', identifyTags: ['platform'] },
        { title: 'OTA升级', key: 'device-ota', identifyTags: ['platform'] },
      ],
    },
    {
      title: '套餐管理',
      key: 'package',
      children: [
        { title: '服务管理', key: 'pkg-service', identifyTags: ['platform'] },
        { title: '赠送套餐', key: 'pkg-gift', identifyTags: ['enterprise'] },
        { title: '开闭管理', key: 'pkg-switch', identifyTags: ['platform', 'enterprise'] },
      ],
    },
    {
      title: '系统管理',
      key: 'system',
      children: [
        { title: '账号管理', key: 'sys-account', identifyTags: ['platform'] },
        { title: '角色管理', key: 'sys-role', identifyTags: ['platform'] },
        { title: '菜单配置', key: 'sys-menu', identifyTags: ['platform'] },
      ],
    },
  ];

  // 模拟角色数据
  const [roles, setRoles] = useState([
    { id: 1, name: '管理员', remark: '拥有系统所有权限', menuKeys: ['home', 'device', 'package', 'system'], createTime: '2023-01-01', accountCount: 1 },
    { id: 2, name: '运维人员', remark: '负责设备运维与OTA', menuKeys: ['home', 'device', 'device-ota'], createTime: '2023-05-20', accountCount: 0 },
    { id: 3, name: '客服专员', remark: '处理用户咨询与套餐赠送', menuKeys: ['home', 'package', 'pkg-gift'], createTime: '2023-08-15', accountCount: 5 },
  ]);

  const showModal = (record = null) => {
    if (record) {
      setEditingRole(record);
      form.setFieldsValue(record);
      setCheckedKeys(record.menuKeys || []);
    } else {
      setEditingRole(null);
      form.resetFields();
      setCheckedKeys([]);
    }
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const roleData = {
        ...values,
        menuKeys: checkedKeys,
      };

      if (editingRole) {
        setRoles(roles.map(r => r.id === editingRole.id ? { ...r, ...roleData } : r));
        message.success('角色修改成功');
      } else {
        setRoles([...roles, { 
          ...roleData, 
          id: Date.now(), 
          createTime: new Date().toISOString().split('T')[0] 
        }]);
        message.success('角色创建成功');
      }
      setIsModalOpen(false);
    } catch (error) {
      console.log('Validate Failed:', error);
    }
  };

  const handleDelete = (id) => {
    setRoles(roles.filter(r => r.id !== id));
    message.success('角色已删除');
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '关联账号',
      dataIndex: 'accountCount',
      key: 'accountCount',
      width: 120,
      align: 'center',
      render: (count, record) => (
        <Button 
          type="link" 
          onClick={() => {
            const roleMap = { '管理员': 'admin', '运维人员': 'operator', '客服专员': 'support' };
            const roleValue = roleMap[record.name] || record.name;
            navigate(`/insideAccount?role=${roleValue}`);
          }}
        >
          {count}
        </Button>
      )
    },
    {
      title: '备注说明',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => showModal(record)}
          >
            编辑
          </Button>
          {record.accountCount > 0 ? (
            <Tooltip title="该角色下存在关联账号，不可删除">
              <Button type="link" danger icon={<DeleteOutlined />} disabled>
                删除
              </Button>
            </Tooltip>
          ) : (
            <Popconfirm
              title="确定要删除该角色吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>内部角色</Title>
      </div>
      <Card 
        // title={<Space><TeamOutlined /><span>角色管理</span></Space>}
        bordered={false}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            新增角色
          </Button>
        }
      >
        <Table 
          columns={columns} 
          dataSource={roles} 
          rowKey="id"
          pagination={{
            showTotal: (total) => `共 ${total} 条记录`,
            showSizeChanger: true,
            defaultPageSize: 10,
          }}
        />
      </Card>

      <Modal
        title={editingRole ? "编辑角色" : "新增角色"}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        width={600}
        destroyOnClose
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="角色名称"
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder="例如：运营主管" />
          </Form.Item>
          
          <Form.Item name="remark" label="备注说明">
            <TextArea rows={2} placeholder="请输入备注说明" />
          </Form.Item>

          <Form.Item label="配置菜单" required>
            <div style={{ 
              border: '1px solid #f0f0f0', 
              borderRadius: '4px', 
              padding: '16px', 
              maxHeight: '400px', 
              overflow: 'auto',
              background: '#fafafa'
            }}>
              <Tree
                checkable
                onCheck={(keys) => setCheckedKeys(keys)}
                checkedKeys={checkedKeys}
                treeData={decorateTreeData(menuTreeData)}
                defaultExpandAll
              />
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GeneralRoleManagement;
